import React, { useState, FC } from 'react'
import { Trans, t } from '@lingui/macro'
import classNames from 'classnames'

import { Modal, ButtonPrimary, ButtonClean } from 'front-end/src/components'

import IconPlus from '../../../../components/common/icon/IconPlusInCircle'
import IconMinus from '../../../../components/common/icon/IconMinus'
import './MoveResourceModal.less'
import {
  MoveTopicMutationVariables,
  useMoveTopicMutation,
  useWorkspacesAndThemesTreeQuery
} from './MoveResourceModal.codegen'

type ResourceMoveTargetType = Omit<MoveTopicMutationVariables, 'topicId'>

export interface IMoveResourceTreeProps {
  topicId: number | null
  currentlyIn: ResourceMoveTargetType
  resolve: () => void
  reject: () => void
}

const lastWorkspaceDestinationIdLocalStorageKey = 'lastWorkspaceDestinationId'

export const MoveResourceModal: FC<IMoveResourceTreeProps> = ({
  topicId,
  currentlyIn,
  resolve,
  reject
}) => {
  const preferredWorkspaceId = localStorage?.getItem(
    lastWorkspaceDestinationIdLocalStorageKey
  )
  const [moveTo] = useMoveTopicMutation()
  const { data } = useWorkspacesAndThemesTreeQuery()
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<number[]>([])
  const [moveTarget, setMoveTarget] = useState<ResourceMoveTargetType | null>(
    null
  )

  if (!data) {
    return null
  }

  let workspaces = [...data?.workspaces]

  if (preferredWorkspaceId) {
    const preferredWorkspaceIndex = workspaces?.findIndex(
      (workspace) => workspace.id === Number(preferredWorkspaceId)
    )
    if (preferredWorkspaceIndex > 0) {
      const workspace = workspaces[preferredWorkspaceIndex]
      workspaces.splice(preferredWorkspaceIndex, 1)
      workspaces.unshift(workspace)
    }
  }

  return (
    <Modal
      header={t`Move resource`}
      subHeader={t`Select where you would like to move this resource`}
      onHide={reject}
      className="MoveResourceModal"
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            onClick={async () => {
              await moveTo({
                variables: {
                  topicId: topicId,
                  ...moveTarget
                } as MoveTopicMutationVariables
              })
              localStorage.setItem(
                lastWorkspaceDestinationIdLocalStorageKey,
                `${moveTarget?.workspaceId}`
              )
              resolve()
            }}
            disabled={
              !moveTarget?.workspaceId ||
              (currentlyIn.workspaceId === moveTarget.workspaceId &&
                moveTarget.themeId === currentlyIn.themeId)
            }
          >
            <Trans>Move</Trans>
          </ButtonPrimary>
        </>
      }
    >
      <ul className="MoveResourceModal__selector">
        {workspaces &&
          workspaces.map((workspace) => {
            return (
              <li
                key={workspace.id}
                className={classNames({
                  expanded: expandedWorkspaces.includes(workspace.id)
                })}
                onClick={() => {
                  setMoveTarget({ workspaceId: workspace.id })
                }}
              >
                <span
                  className={classNames({
                    selected: moveTarget?.workspaceId === workspace.id
                  })}
                >
                  {(workspace.themes.length &&
                    ((expandedWorkspaces.includes(workspace.id) && (
                      <IconMinus
                        onClick={() => {
                          setExpandedWorkspaces(
                            expandedWorkspaces.filter(
                              (ws) => ws !== workspace.id
                            )
                          )
                        }}
                      />
                    )) || (
                      <IconPlus
                        onClick={() => {
                          setExpandedWorkspaces([
                            workspace.id,
                            ...expandedWorkspaces
                          ])
                        }}
                      />
                    ))) ||
                    null}
                  {workspace.name}
                </span>
                {expandedWorkspaces.includes(workspace.id) &&
                  workspace.themes.length && (
                    <ul>
                      {workspace.themes.map((theme) => {
                        return (
                          <li
                            key={theme.id}
                            className={classNames({
                              selected: moveTarget?.themeId === theme.id
                            })}
                            onClick={(e) => {
                              e.stopPropagation()

                              setMoveTarget({
                                workspaceId: workspace.id,
                                themeId: theme.id
                              })
                            }}
                          >
                            {theme.title}
                          </li>
                        )
                      })}
                    </ul>
                  )}
              </li>
            )
          })}
      </ul>
    </Modal>
  )
}
